import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';

import intlShape from '@/shared/utils/intlShape';
import AuthService, { functionalities } from '@/shared/services/AuthService';
import { CanCreate } from '@/shared/components/security';
import { getInvoiceStreamNames } from '@/shared/services/InvoiceStreamService';
import ToastrService from '@/shared/services/ToastrService';
import guid from '@/shared/utils/guid';
import TimeInput from '../input/TimeInput';
import NumberInput from '../input/NumberInput';
import Checkbox from '../input/Checkbox';
import messages from './registrationMessages';
import ZendeskHelp from '@/shared/components/ZendeskHelp';
import RelationTagsOnRegistration from './RelationTagsOnRegistration';
import ResourceAutoComplete from './ResourceAutoComplete';
import InteractionAttachments from './InteractionAttachments';
import CalcDistanceModal from './CalcDistanceModal';
import UserFormGroup from './partials/UserFormGroup';
import {
  actions,
  defaultOptions,
  getAssignmentTemplateId,
  getFirstSelectedInvoiceStreamId,
} from './registrationHelpers';
import {
  FormGroupPart,
  UnitPricePart,
  DiscountPart,
  CostQuantityPart,
  DurationFormGroup,
  VatPercentagePart,
  TotalAmountPart,
  RelationFormGroup,
  InteractionFormGroup,
  AssignmentFormGroup,
  TaskFormGroup,
  OwnCompanyRelationFormGroup,
  DatePart,
  ReferenceYearPart,
} from './partials';
import { retrieveResources, getInForfaitAndPrice, getLastRegistrationTime } from './resourceApi';
import {
  GROUP_PARTS,
  PART_TYPE,
  groupRepresentative,
  partHasContent,
} from './registrationGroupHelpers';

// Panels with content (an existing registration) start expanded; otherwise only Time is open.
const initialCollapsed = group => {
  const anyContent = GROUP_PARTS.some(part => partHasContent(group[part]));
  return {
    work: anyContent ? !partHasContent(group.work) : false,
    travel: !partHasContent(group.travel),
    cost: !partHasContent(group.cost),
    description: true,
    communication: true,
  };
};

/**
 * The grouped registration form: a shared header plus three collapsible panels (time,
 * product/cost and distance) that are entered together and saved as one group. Reuses the
 * same leaf field components as the single-registration editor, but orchestrates the
 * resource/invoice-stream/price loading once at group level and fans shared header changes
 * out to all three sub-registrations.
 */
class RegistrationGroupComponent extends Component {
  hasPriceListPermission = AuthService.hasPermission({
    functionality: functionalities.Pricelist,
    privilege: 'Read',
  });

  state = {
    resourcesByType: { 0: null, 1: null, 2: null },
    invoiceStreams: null,
    collapsed: initialCollapsed(this.props.group),
    showDistanceModal: false,
  };

  onDistanceCalculated = ({ distance, description }) => {
    this.setState({ showDistanceModal: false });
    if (distance) this.part('travel', actions.quantityChanged, distance);
    if (description) {
      const current = groupRepresentative(this.props.group).remark;
      this.shared(actions.remarkChanged, `${current ? `${current}\n` : ''}${description}`);
    }
  };

  componentDidMount() {
    GROUP_PARTS.forEach(part => this.retrieveResources(PART_TYPE[part]));
    const rep = groupRepresentative(this.props.group);
    if (rep.relation?.id) this.loadInvoiceStreams(rep.relation.id, { updatePrice: false });

    // Preset the time panel's start to the user's next free slot, like the legacy dialog.
    const { work } = this.props.group;
    if (!work.id && work.date) {
      getLastRegistrationTime(work.date).then(value => {
        if (value) this.part('work', actions.fromMinutesChanged, value);
      });
    }
  }

  setPriceLoading = loading => {
    const { onPriceLoadingChanged } = this.props;
    if (onPriceLoadingChanged) onPriceLoadingChanged(loading);
  };

  shared = (action, value) => this.props.onSharedAction(action, value);

  part = (partName, action, value) => this.props.onPartAction(partName, action, value);

  retrieveResources = resourceType => {
    const { intl } = this.props;
    retrieveResources(intl, resourceType).then(resources => {
      this.setState(state => ({
        resourcesByType: { ...state.resourcesByType, [resourceType]: resources },
      }));
    });
  };

  loadInvoiceStreams = (relationId, { updatePrice = false, warnWhenEmpty = false } = {}) => {
    this.setState({ invoiceStreams: null });
    if (!relationId) return;
    getInvoiceStreamNames(relationId).then(invoiceStreams => {
      const rep = groupRepresentative(this.props.group);
      if (rep.relation?.id !== relationId) return;
      if (warnWhenEmpty && !invoiceStreams.length) {
        const { intl } = this.props;
        ToastrService.warning(
          intl.formatMessage(messages.YouCannotSelectThisRelationBecauseItHasNoInvoiceStreams),
        );
        this.shared(actions.relationChanged, null);
        return;
      }
      this.setState({ invoiceStreams });
      this.shared(actions.invoiceStreamsLoaded, invoiceStreams);

      if (!updatePrice || !this.hasPriceListPermission) return;
      const provisionalId = rep.relation?.invoiceStreamId ?? null;
      const invoiceStreamId =
        !rep.invoiceStreamId || rep.invoiceStreamId === provisionalId
          ? getFirstSelectedInvoiceStreamId(invoiceStreams, provisionalId)
          : rep.invoiceStreamId;
      if (!invoiceStreamId) return;
      GROUP_PARTS.forEach(partName => {
        const reg = this.props.group[partName];
        if (reg.resource?.id) {
          this.setPriceLoading(true);
          getInForfaitAndPrice(invoiceStreamId, reg.resource, reg.userId)
            .then(priceInfo => this.part(partName, actions.priceInfoChanged, priceInfo))
            .finally(() => this.setPriceLoading(false));
        }
      });
    });
  };

  // --- Shared header handlers -------------------------------------------------
  onDateChanged = date => {
    this.shared(actions.dateChanged, date);
    getLastRegistrationTime(date).then(fromMinutes => {
      if (fromMinutes) this.part('work', actions.fromMinutesChanged, fromMinutes);
    });
  };

  onRelationChanged = relation => {
    this.shared(actions.relationChanged, relation);
    this.loadInvoiceStreams(relation?.id, { updatePrice: true, warnWhenEmpty: true });
  };

  onAssignmentChanged = invoiceStreamId => {
    const { invoiceStreams } = this.state;
    this.shared(actions.assignmentChanged, {
      invoiceStreamId,
      assignmentTemplateId: getAssignmentTemplateId(invoiceStreams, invoiceStreamId),
    });
    if (!this.hasPriceListPermission || !invoiceStreamId) return;
    GROUP_PARTS.forEach(partName => {
      const reg = this.props.group[partName];
      if (reg.resource?.id) {
        this.setPriceLoading(true);
        getInForfaitAndPrice(invoiceStreamId, reg.resource, reg.userId)
          .then(priceInfo => this.part(partName, actions.priceInfoChanged, priceInfo))
          .finally(() => this.setPriceLoading(false));
      }
    });
  };

  onTaskChanged = task => {
    if (!task || !task.id) {
      this.shared(actions.taskChanged, null);
      return;
    }
    this.shared(actions.taskChanged, task);
    
    const { resourcesByType } = this.state;
    const resource = task.resourceId
      ? (resourcesByType[PART_TYPE.work] || []).find(x => x.id === task.resourceId)
      : null;
    if (resource) this.onResourceChanged('work', resource);
  };

  // --- Per-part resource / price ----------------------------------------------
  getResourceChangedObject = (partName, resource) => {
    const reg = this.props.group[partName];
    if (this.hasPriceListPermission) {
      const { invoiceStreams } = this.state;
      const invoiceStreamId =
        reg.invoiceStreamId ||
        getFirstSelectedInvoiceStreamId(invoiceStreams, reg.relation?.invoiceStreamId) ||
        null;
      if (resource?.id && invoiceStreamId) {
        this.setPriceLoading(true);
        return getInForfaitAndPrice(invoiceStreamId, resource, reg.userId)
          .then(priceInfo => ({ ...priceInfo, resource }))
          .finally(() => this.setPriceLoading(false));
      }
    }
    return Promise.resolve({ resource, inForfait: null, priceOptions: null, price: null });
  };

  onResourceChanged = (partName, resource) => {
    this.getResourceChangedObject(partName, resource).then(value => {
      this.part(partName, actions.resourceChanged, value);
      this.setState(state => ({ collapsed: { ...state.collapsed, [partName]: false } }));
    });
  };

  toggleCollapsed = partName =>
    this.setState(state => ({
      collapsed: { ...state.collapsed, [partName]: !state.collapsed[partName] },
    }));

  renderPartBody(partName) {
    const { disabled, showValidation, intl } = this.props;
    const reg = this.props.group[partName];
    const resources = this.state.resourcesByType[PART_TYPE[partName]];
    const invalidResource = !reg.resource || !reg.resource.id;
    // Nothing in a panel is editable until a relation has been picked.
    const noRelation = !reg.relation?.id;
    // Each part is optional; only flag validation errors once it has a resource picked, so
    // an empty distance/cost panel never lights up red.
    const partValidation = showValidation && partHasContent(reg);

    return (
      <>
        <div className="form-group">
          <FormGroupPart
            label={messages.PriceListItem}
            inputCols="col-sm-10"
            invalid={partValidation && invalidResource}
          >
            {resources && resources.length > 0 ? (
              <ResourceAutoComplete
                items={resources}
                placeholder={intl.formatMessage(messages.SearchPriceListItem)}
                onChange={resource => this.onResourceChanged(partName, resource)}
                emptyLabel={intl.formatMessage(messages.NoPriceListItemsFound)}
                value={reg.resource}
                required
                disabled={disabled || noRelation}
                crmType={reg.relation?.crm}
                assignmentTemplateId={reg.assignmentTemplateId}
                intl={intl}
                isSmall
              />
            ) : (
              <FormattedMessage {...messages.NoPriceListItemsFound} />
            )}
          </FormGroupPart>
        </div>

        {partName === 'work' && (
          <>
            <div className="form-group">
              <FormGroupPart label={messages.From}>
                <TimeInput
                  value={reg.fromMinutes}
                  id="FromMinutes"
                  disabled={invalidResource || disabled}
                  onChange={value => this.part('work', actions.fromMinutesChanged, value)}
                />
              </FormGroupPart>
              <FormGroupPart label={messages.Until} alignLabel="left">
                <TimeInput
                  value={reg.toMinutes}
                  id="ToMinutes"
                  disabled={invalidResource || disabled}
                  onChange={value => this.part('work', actions.toMinutesChanged, value)}
                />
              </FormGroupPart>
            </div>
            <DurationFormGroup
              duration={reg.durationMinutes}
              onChange={value => this.part('work', actions.durationMinutesChanged, value)}
              disabled={invalidResource || disabled}
              showValidation={partValidation}
              max={1439 - (reg.fromMinutes || 0)}
              intl={intl}
            />
          </>
        )}

        {partName === 'cost' && (
          <div className="form-group">
            <CostQuantityPart
              quantity={reg.quantity}
              onChange={value => this.part('cost', actions.quantityChanged, value)}
              disabled={invalidResource || disabled}
              showValidation={partValidation}
            />
          </div>
        )}

        {partName === 'travel' && (
          <div className="form-group">
            <label className="control-label col-sm-2">
              {intl.formatMessage(messages.Distance)}
            </label>
            <div
              className={classNames('col-sm-4', { 'has-error': partValidation && !reg.quantity })}
            >
              <div
                className={classNames('input-group input-group-sm', {
                  disabled: invalidResource || disabled,
                })}
              >
                <span className="input-group-addon">km</span>
                <NumberInput
                  id="TravelDistanceField"
                  min={0}
                  disabled={invalidResource || disabled}
                  value={reg.quantity}
                  onChange={value => this.part('travel', actions.quantityChanged, value)}
                  className="form-control input-sm"
                />
                {!invalidResource && !disabled && (
                  <span className="input-group-btn">
                    <button
                      type="button"
                      className="btn btn-default"
                      title={intl.formatMessage(messages.CalculateDistance)}
                      onClick={() => this.setState({ showDistanceModal: true })}
                    >
                      <i className="fa fa-calculator" />
                    </button>
                  </span>
                )}
              </div>
            </div>
            {this.hasPriceListPermission && (
              <div className="col-sm-4 col-sm-offset-2">
                <Checkbox
                  id="IsBillableField-travel"
                  value={reg.isBillable}
                  text={intl.formatMessage(messages.Billable)}
                  disabled={invalidResource || disabled}
                  onClick={() => this.part('travel', actions.isBillableChanged, !reg.isBillable)}
                />
              </div>
            )}
          </div>
        )}

        {this.hasPriceListPermission && (
          <>
            <div className="form-group">
              <UnitPricePart
                unitPrice={reg.amount}
                price={reg.price}
                priceOptions={reg.priceOptions}
                isFixedPrice={reg.isFixedPrice}
                intl={intl}
                onUnitPriceChanged={value => this.part(partName, actions.unitPriceChanged, value)}
                onPriceChanged={value => this.part(partName, actions.priceChanged, value)}
                disabled={invalidResource || disabled}
              />
              <DiscountPart
                discount={reg.discountPercentage}
                onChange={value => this.part(partName, actions.discountChanged, value)}
                disabled={invalidResource || disabled}
              />
            </div>
            <div className="form-group">
              <VatPercentagePart
                vatPercentage={reg.vatPercentage}
                vatType={reg.vatType}
                onChange={value => this.part(partName, actions.vatPercentageChanged, value)}
                disabled={invalidResource || disabled}
              />
              <TotalAmountPart
                value={reg.totalAmount}
                isFixedPrice={reg.isFixedPrice}
                intl={intl}
                onChange={value => this.part(partName, actions.totalAmountChanged, value)}
                disabled={invalidResource || disabled}
              />
            </div>
          </>
        )}

        <div className="form-group">
          {partName === 'work' && (
            <div className="col-sm-4 col-sm-offset-2">
              <Checkbox
                id={`IsBillableField-${partName}`}
                value={reg.isBillable}
                text={intl.formatMessage(messages.Billable)}
                disabled={invalidResource || disabled}
                onClick={() => this.part(partName, actions.isBillableChanged, !reg.isBillable)}
              />
            </div>
          )}
          {reg.inForfait !== undefined && reg.inForfait !== null && (
            <div className="col-sm-4 col-sm-offset-2">
              <Checkbox
                id={`InForfaitField-${partName}`}
                value={reg.inForfait}
                text={intl.formatMessage(messages.InForfait)}
                disabled={invalidResource || disabled}
                onClick={() => this.part(partName, actions.inForfaitChanged, !reg.inForfait)}
              />
            </div>
          )}
        </div>
      </>
    );
  }

  renderCollapsible(key, titleNode, body) {
    const collapsed = this.state.collapsed[key];
    return (
      <div className="control-panel margin10T" key={key}>
        <div className={classNames('title-block', collapsed ? 'collapsed' : 'expanded')}>
          <h2 className="pointer" onClick={() => this.toggleCollapsed(key)}>
            <i
              className={classNames('fa margin5R', collapsed ? 'fa-chevron-down' : 'fa-chevron-up')}
            />
            {titleNode}
          </h2>
        </div>
        {!collapsed && <div className="control-panel-body">{body}</div>}
      </div>
    );
  }

  renderPanel(partName, title) {
    return this.renderCollapsible(partName, <span>{title}</span>, this.renderPartBody(partName));
  }

  render() {
    const { disabled, showValidation, intl, options: externalOptions } = this.props;
    const options = { ...defaultOptions, ...(externalOptions || {}) };
    const { invoiceStreams } = this.state;
    const rep = groupRepresentative(this.props.group);
    const noRelation = !rep.relation?.id;

    const showAssignments =
      options.showAssignments && rep.relation && (invoiceStreams || []).length > 1;
    const showTasks =
      options.showTasks && !!rep.invoiceStreamId && rep.invoiceStreamId !== guid.empty;
    const showOwnCompanyRelation = options.ownCompanyRelation;
    const focusRelationInput = options.showRelation && options.editableRelation && !rep.relation;
    const showReferenceYear =
      options.showReferenceYear && AuthService.hasFeature('ReferenceYear') && !!rep.relation;

    return (
      <>
        <div className="panel">
          <div className="panel-body">
            {options.showOtherUsers && !!rep.userId && rep.userId !== AuthService.getUserId() && (
              <UserFormGroup userId={rep.userId} />
            )}

            {options.showRelation && showOwnCompanyRelation && (
              <OwnCompanyRelationFormGroup
                relation={rep.relation}
                readOnly={!options.editableRelation}
                disabled={disabled}
                autoFocus={focusRelationInput}
                onChange={this.onRelationChanged}
                userId={rep.userId}
              />
            )}
            {options.showDate && (
              <div className="form-group">
                <DatePart
                  value={rep.date}
                  onChange={this.onDateChanged}
                  disabled={disabled}
                  showValidation={showValidation}
                  inputCols="col-sm-10"
                  intl={intl}
                />
              </div>
            )}
            {options.showRelation && !showOwnCompanyRelation && (
              <RelationFormGroup
                relation={rep.relation}
                readOnly={!options.editableRelation}
                disabled={disabled}
                onChange={this.onRelationChanged}
                autoFocus={focusRelationInput}
                showValidation={showValidation}
                crmTypes={options.crmTypes}
                activeOn={rep.date}
                showOpenRelation
              />
            )}
            {showAssignments && (
              <AssignmentFormGroup
                invoiceStreams={invoiceStreams || []}
                readOnly={!options.editableRelation}
                value={rep.invoiceStreamId}
                disabled={disabled}
                onChange={this.onAssignmentChanged}
                intl={intl}
              />
            )}
            {showTasks && (
              <TaskFormGroup
                plannedTaskId={rep.plannedTaskId}
                invoiceStreamId={rep.invoiceStreamId}
                onChange={this.onTaskChanged}
                disabled={disabled}
                showValidation={showValidation}
              />
            )}
            {showReferenceYear && (
              <div className="form-group">
                <ReferenceYearPart
                  value={rep.referenceYear}
                  onChange={value => this.shared(actions.referenceYearChanged, value)}
                  disabled={disabled}
                  showValidation={showValidation}
                  inputCols="col-sm-10"
                  intl={intl}
                />
              </div>
            )}
            {options.showRelation && (
              <RelationTagsOnRegistration key="groupRelationTags" relationId={rep.relation?.id} />
            )}
          </div>
        </div>

        {this.renderPanel('work', intl.formatMessage(messages.Time))}
        {this.renderPanel('travel', intl.formatMessage(messages.Distance))}
        {this.renderPanel('cost', intl.formatMessage(messages.ProductCost))}

        {this.renderCollapsible(
          'description',
          <span>{intl.formatMessage(messages.Description)}</span>,
          <>
            <div className="form-group">
              <FormGroupPart
                label={messages.InvoiceRemark}
                inputCols="col-sm-9"
                labelCols="col-sm-3"
              >
                <textarea
                  id="GroupRemarkField"
                  className="form-control vertical-resize"
                  value={rep.remark || ''}
                  disabled={disabled || noRelation}
                  onChange={e => this.shared(actions.remarkChanged, e.target.value)}
                  placeholder={intl.formatMessage(messages.InvoiceRemark)}
                  maxLength="3000"
                />
              </FormGroupPart>
            </div>
            <div className="form-group">
              <FormGroupPart
                label={messages.InternalRemark}
                inputCols="col-sm-9"
                labelCols="col-sm-3"
              >
                <textarea
                  id="GroupInternalRemarkField"
                  className="form-control vertical-resize"
                  value={rep.internalRemark || ''}
                  disabled={disabled || noRelation}
                  onChange={e => this.shared(actions.internalRemarkChanged, e.target.value)}
                  placeholder={intl.formatMessage(messages.InternalRemark)}
                  maxLength="3000"
                />
              </FormGroupPart>
            </div>
          </>,
        )}

        <CanCreate on="InteractionsOwn">
          {this.renderCollapsible(
            'communication',
            <>
              {intl.formatMessage(messages.CommunicationReport)}
              <ZendeskHelp article="5434756079122" />
            </>,
            <InteractionFormGroup
              interactionType={rep.interactionType}
              interactionTitle={rep.interactionTitle}
              interactionDescription={rep.interactionDescription}
              doAction={(action, value) => this.shared(action, value)}
              disabled={disabled || noRelation}
              intl={intl}
              belowTypeButtons={
                <InteractionAttachments
                  attachments={this.props.attachments}
                  onChange={this.props.onAttachmentsChange}
                  disabled={disabled || !rep.relation?.id}
                  intl={intl}
                />
              }
            />,
          )}
        </CanCreate>

        {this.state.showDistanceModal && rep.relation?.id && (
          <CalcDistanceModal
            show
            relationId={rep.relation.id}
            companyId={rep.relation.companyId}
            onClose={() => this.setState({ showDistanceModal: false })}
            onSave={this.onDistanceCalculated}
          />
        )}
      </>
    );
  }
}

RegistrationGroupComponent.propTypes = {
  group: PropTypes.shape({}).isRequired,
  onSharedAction: PropTypes.func.isRequired,
  onPartAction: PropTypes.func.isRequired,
  onPriceLoadingChanged: PropTypes.func,
  options: PropTypes.shape({}),
  disabled: PropTypes.bool,
  showValidation: PropTypes.bool,
  attachments: PropTypes.arrayOf(PropTypes.shape({})),
  onAttachmentsChange: PropTypes.func,
  intl: intlShape.isRequired,
};

RegistrationGroupComponent.defaultProps = {
  onPriceLoadingChanged: undefined,
  options: {},
  disabled: false,
  showValidation: false,
  attachments: [],
  onAttachmentsChange: () => {},
};

export default RegistrationGroupComponent;
